import { useEffect, useReducer } from 'react';

import { PROCESSING_EXPIRY_MS } from 'modules/common/components/VerifiedStatementUpload/utils/statementVerdict';

/**
 * Re-render when the next unanswered upload ages out.
 *
 * `hasProcessingExpired` reads the clock during render, which only tells the truth if something
 * renders as the clock crosses the boundary — and in the one case it exists for, nothing does.
 * A file the server never answers for produces poll after poll of the same payload, and
 * react-query hands back the identical object for an unchanged response, so no observer
 * re-renders; then the poll stops asking at `PROCESSING_EXPIRY_MS` for the same reason. Left
 * alone the modal keeps Save disabled and the delete row hidden for the rest of the session,
 * and closing and reopening it is the member's only way out — which is the state the expiry
 * was added to prevent.
 *
 * One timeout at the nearest boundary rather than a repeating tick: `uploadedAt` says exactly
 * when each file ages out, so there is nothing to discover by re-reading the clock, and an
 * interval on a modal that may sit open all session is a wake-up per tick to learn nothing.
 *
 * Takes every upload's timestamp, not just the ones currently reading as in flight. Filtering
 * would need a verdict, and the verdict a caller holds is the *resolved* one, where a member's
 * staged month already reads as `matched` — so the filter would drop precisely the files this
 * exists for. One render nobody needed is far cheaper than a boundary nobody saw.
 *
 * Returns a counter that changes on each boundary crossed, for a caller that derives its clock
 * read inside a `useMemo`. Forcing a render is only half the job there: the memo has to be told
 * the clock is one of its inputs, or it hands back the same answer it computed before the
 * boundary and the wake-up buys nothing. A caller reading the clock during render needs nothing
 * from this.
 */
export default function useProcessingExpiryTick(uploadedAts: readonly (string | undefined)[]): number {
    const [tickCount, tick] = useReducer((count: number) => count + 1, 0);

    // Reduced to one number before the effect sees it, so the timer is re-armed when the
    // deadline genuinely moves and not merely because the caller built a fresh array.
    const nextExpiryAt = uploadedAts.reduce<number | null>((soonest, uploadedAt) => {
        if (!uploadedAt) {
            return soonest;
        }
        const expiresAt = new Date(uploadedAt).getTime() + PROCESSING_EXPIRY_MS;
        // An unparseable timestamp ages against nothing, and a boundary already behind us has
        // been observed by this very render — neither is something to wake up for.
        if (!Number.isFinite(expiresAt) || expiresAt < Date.now()) {
            return soonest;
        }
        return soonest === null || expiresAt < soonest ? expiresAt : soonest;
    }, null);

    useEffect(() => {
        if (nextExpiryAt === null) {
            return undefined;
        }
        // Land strictly past the boundary: `hasProcessingExpired` compares with `>`, so waking
        // exactly on it would re-render to the same answer and schedule nothing further.
        const timer = setTimeout(tick, nextExpiryAt - Date.now() + 1);
        return () => clearTimeout(timer);
    }, [nextExpiryAt]);

    return tickCount;
}
